<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-warning header-enhanced">
      <!-- Moderation Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Pending Reviews"
            type="gradient-warning"
            :sub-title="stats.pendingReviews ? stats.pendingReviews.toLocaleString() : 'Loading...'"
            icon="ni ni-time-alarm"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">⏰ Awaiting</span>
              <span class="text-nowrap">moderation</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Resolved Today"
            type="gradient-success"
            :sub-title="stats.resolvedToday ? stats.resolvedToday.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">✓ Completed</span>
              <span class="text-nowrap">today</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Reports"
            type="gradient-danger"
            :sub-title="stats.totalReports ? stats.totalReports.toLocaleString() : 'Loading...'"
            icon="ni ni-notification-70"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-danger mr-2">⚠️ Reported</span>
              <span class="text-nowrap">this month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Avg Response"
            type="gradient-info"
            sub-title="2.4 hours"
            icon="ni ni-watch-time"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">📊 Average</span>
              <span class="text-nowrap">response time</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-shield-check mr-2"></i>Content Moderation
          </h3>
          <p class="text-white-50 mb-0">Review and moderate reported content</p>
        </b-col>
        <b-col class="text-right">
          <b-badge variant="light" class="mr-2">
            <i class="ni ni-time-alarm mr-1"></i>{{ stats.pendingReviews || 0 }} Pending
          </b-badge>
          <b-button
            variant="light"
            size="sm"
            class="shadow-sm"
            @click="refreshContent">
            <i class="ni ni-refresh mr-1"></i>Refresh
          </b-button>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <b-row>
        <b-col xl="9">
          <b-card class="content-grid-card border-0 shadow">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0 text-dark">
                  <i class="ni ni-image mr-2 text-primary"></i>
                  Reported Content
                </h5>
                <b-badge variant="warning" v-if="issues && Object.keys(issues).length > 0">
                  {{ Object.keys(issues).length }} items
                </b-badge>
              </div>
            </template>

            <div v-if="!issues || Object.keys(issues).length === 0" class="text-center py-5">
              <i class="ni ni-check-bold text-success" style="font-size: 3rem;"></i>
              <h4 class="mt-3 text-muted">All Clear!</h4>
              <p class="text-muted">No content reports pending review</p>
            </div>

            <b-row v-else>
              <b-col
                :cols="colNum"
                v-for="(issue, index) in issues"
                :key="index">
                <b-card
                  class="content-card mb-4 border-0 shadow-sm"
                  @click="selectCard(issue)"
                  no-body>
                  <div class="content-image-container">
                    <img
                      :src="issue.content[0].url"
                      class="content-image"
                      alt="Reported content"
                      @error="setPlaceholderImage" />
                    <div class="content-overlay">
                      <div class="content-badges">
                        <b-badge variant="danger" class="mr-1">
                          <i class="ni ni-notification-70 mr-1"></i>Report
                        </b-badge>
                        <b-badge variant="warning">
                          <i class="ni ni-time-alarm mr-1"></i>Pending
                        </b-badge>
                      </div>
                      <div class="view-details">
                        <i class="ni ni-bold-right text-white"></i>
                      </div>
                    </div>
                  </div>
                  <b-card-body class="p-3">
                    <div class="d-flex align-items-center mb-2">
                      <div class="owner-avatar mr-3">
                        <i class="ni ni-single-02 text-primary"></i>
                      </div>
                      <div class="flex-fill">
                        <h6 class="mb-0 font-weight-600 text-dark">
                          {{ issue.contentOwner[0].firstname }}
                          {{ issue.contentOwner[0].lastname }}
                        </h6>
                        <small class="text-muted">Content Owner</small>
                      </div>
                    </div>
                    <div class="content-meta">
                      <small class="text-muted">
                        <i class="ni ni-calendar-grid-58 mr-1"></i>
                        Reported recently
                      </small>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <template #footer v-if="issues && Object.keys(issues).length > 0">
              <div class="pagination-container">
                <b-pagination
                  class="mb-0"
                  align="center"
                  :per-page="issuesPerPage"
                  size="md"
                  :total-rows="issuesTotal"
                  v-model="currentPage"
                  pills>
                </b-pagination>
                <div class="pagination-info mt-2">
                  <small class="text-muted">
                    Showing {{ (currentPage - 1) * issuesPerPage + 1 }} to
                    {{ Math.min(currentPage * issuesPerPage, issuesTotal) }} of
                    {{ issuesTotal }} reports
                  </small>
                </div>
              </div>
            </template>
          </b-card>
        </b-col>
        <b-col xl="3">
          <b-card class="sidebar-card border-0 shadow">
            <template #header>
              <h5 class="mb-0 text-dark">
                <i class="ni ni-chart-bar-32 mr-2 text-primary"></i>
                Moderation Stats
              </h5>
            </template>
            <div class="stats-list">
              <div class="stat-item">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="stat-label">Pending Reviews</span>
                  <b-badge variant="warning">{{ stats.pendingReviews || 0 }}</b-badge>
                </div>
              </div>
              <div class="stat-item">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="stat-label">Resolved Today</span>
                  <b-badge variant="success">{{ stats.resolvedToday || 0 }}</b-badge>
                </div>
              </div>
              <div class="stat-item">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="stat-label">Total Reports</span>
                  <b-badge variant="danger">{{ stats.totalReports || 0 }}</b-badge>
                </div>
              </div>
            </div>
          </b-card>

          <view-card @update-issues="updateIssues" class="mt-4"></view-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  const ViewCard = () => import('./ViewCard.vue');
  import BreakpointMixin from '@/mixins/BreakpointMixin';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  export default {
    name   : 'ModerateContent',
    mixins : [
      BreakpointMixin,
    ],
    data() {
      return {
        issuesCurrentPage : 0,
        issuesLastPage    : 0,
        issuesPerPage     : 8,
        issuesTotal       : 0,
        issues            : {},
        currentPage       : 1,
        stats: {
          pendingReviews: null,
          resolvedToday: null,
          totalReports: null,
        },
      }
    },
    components : {
      ViewCard,
      baseHeader,
      StatsCard
    },
    computed : {
      col() {
        return 'col-' + this.getPortion(4);
      },
      colNum() {
        return this.getPortion(4);
      },
    },
    watch : {
      currentPage(value) {
        this.getIssues(value - 1);
      },
    },
    methods : {

      /**
       * Selected Card
       */
      selectCard(issue) {
        this.$router.push({ path : '/admin/moderate-content/' + issue.ucc_id });
      },

      /**
       * Get All Contested Issues
       */
      getIssues(currentPage = this.issuesCurrentPage) {
        this.$http.get('api/issue/admin', {
          params : {
            page    : currentPage + 1,
            perPage : this.issuesPerPage,
          },
        }).then(response => {
          this.issues = response.data.data.issues;
          this.issuesCurrentPage = response.data.data.currentPage;
          this.issuesLastPage = response.data.data.lastPage;
          this.issuesTotal = response.data.data.total;
        });
      },

      updateIssues() {
        this.issuesCurrentPage = 0;
        this.issuesTotal = 0;
        this.issues = {};
        this.currentPage = 1;

        this.getIssues();
      },

      /**
       * Initialize Moderation Statistics
       */
      initializeStats() {
        // Simulated moderation statistics - replace with actual API calls
        this.stats.pendingReviews = 12;
        this.stats.resolvedToday = 8;
        this.stats.totalReports = 156;
      },

      /**
       * Refresh Content
       */
      refreshContent() {
        this.initializeStats();
        this.updateIssues();
      },

      /**
       * Set Placeholder Image
       */
      setPlaceholderImage(event) {
        event.target.src = require('../../../assets/images/content-placeholder.png');
      },
    },
    mounted() {
      this.initializeStats();
      this.getIssues(0);
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/settings";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9) 0%, rgba(255, 152, 0, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 193, 7, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 152, 0, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Content Grid */
.content-grid-card {
  border-radius: 16px !important;
  animation: fadeInUp 0.8s ease-out;
}

.content-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
}

.content-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15) !important;
}

.content-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.content-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.content-card:hover .content-image {
  transform: scale(1.05);
}

.content-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  opacity: 0;
  transition: all 0.3s ease;
}

.content-card:hover .content-overlay {
  opacity: 1;
}

.content-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.view-details {
  align-self: flex-end;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 193, 7, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.3s ease;
}

.view-details:hover {
  background: rgba(255, 193, 7, 1);
  transform: scale(1.1);
}

.owner-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(94, 114, 228, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.content-meta {
  margin-top: 8px;
}

/* Enhanced Sidebar */
.sidebar-card {
  border-radius: 12px !important;
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.stats-list {
  padding: 0;
}

.stat-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-weight: 500;
  color: #495057;
}

/* Enhanced Pagination */
.pagination-container {
  padding: 20px;
  background: rgba(248, 249, 250, 0.5);
  border-radius: 0 0 16px 16px;
}

.pagination-info {
  text-align: center;
  color: #6c757d;
  font-weight: 500;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media only screen and (max-width: $xs-max) {
  .stats-card-hover:hover {
    transform: none;
  }

  .content-card:hover {
    transform: none;
  }

  .content-card:hover .content-overlay {
    opacity: 1;
  }

  .content-image-container {
    height: 150px;
  }
}

@media only screen and (min-width: $sm-min) {
  .content-image-container {
    height: 180px;
  }
}

@media only screen and (min-width: $md-min) {
  .content-image-container {
    height: 200px;
  }
}

@media only screen and (min-width: $lg-min) {
  .content-image-container {
    height: 220px;
  }
}

@media only screen and (min-width: $xl-min) {
  .content-image-container {
    height: 180px;
  }

  .content-card .card-body {
    padding: 0.75rem;
  }
}

@media only screen and (min-width: $special) {
  .content-card .card-body {
    padding: 0.75rem;
  }
}

@media only screen and (min-width: $xl2-min) {
  .content-image-container {
    height: 200px;
  }
}

@media only screen and (min-width: $xl5-min) {
  .content-image-container {
    height: 220px;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}

table {
  z-index: 99;
  position: relative;
  background: white;
}
</style>
